/**
 * Cart Drawer Upsell Slider
 *
 * Initializes a Swiper slider for upsell recommendations in the cart drawer.
 * Handles:
 * - Swiper initialization and re-initialization on cart updates
 * - Shopify Recommendations API fallback when Liquid yields no results
 * - Add-to-cart from upsell cards
 */

function initCartUpsellsSwiper() {
	const container = document.querySelector(".cart-upsells__slider");
	if (!container) return;

	if (container.swiper) {
		container.swiper.destroy(true, true);
	}

	new Swiper(container, {
		slidesPerView: "auto",
		spaceBetween: 10,
		freeMode: true,
		watchOverflow: true,
		navigation: {
			nextEl: ".cart-upsells__next",
			prevEl: ".cart-upsells__prev",
		},
	});
}

async function fetchCartUpsellsFallback() {
	const container = document.querySelector(
		'.cart-upsells[data-needs-fetch="true"]'
	);
	if (!container) return;

	const cartProductIds = (container.dataset.cartProductIds || "")
		.split(",")
		.filter(Boolean)
		.map(Number);

	if (cartProductIds.length === 0) return;

	const heading = container.dataset.heading || "You May Also Like";
	const max = parseInt(container.dataset.max || "8", 10);
	const imageRatio = container.dataset.imageRatio || "portrait";
	const currencyCode = container.dataset.currencyCode === "true";

	const seenIds = new Set(cartProductIds);
	const seenTitles = new Set();
	const recommendations = [];

	// Get cart item titles to exclude same-name products
	const cartItems = document.querySelectorAll(".cart-item__name");
	cartItems.forEach((el) => {
		const title = (el.textContent || "").trim().toLowerCase();
		if (title) seenTitles.add(title);
	});

	// Fetch for up to 3 unique cart products to limit API calls
	const uniqueIds = [...new Set(cartProductIds)].slice(0, 3);

	for (const productId of uniqueIds) {
		if (recommendations.length >= max) break;
		try {
			const response = await fetch(
				`/recommendations/products.json?product_id=${productId}&limit=8`
			);
			if (!response.ok) continue;
			const data = await response.json();

			for (const product of data.products || []) {
				if (recommendations.length >= max) break;
				if (seenIds.has(product.id)) continue;
				if (!product.available) continue;
				const titleLower = (product.title || "").toLowerCase();
				if (seenTitles.has(titleLower)) continue;
				seenIds.add(product.id);
				seenTitles.add(titleLower);
				recommendations.push(product);
			}
		} catch (e) {
			// Silently continue on fetch failure
		}
	}

	if (recommendations.length === 0) return;

	container.removeAttribute("data-needs-fetch");
	container.innerHTML = buildUpsellsHTML(
		recommendations,
		heading,
		imageRatio,
		currencyCode
	);
	initCartUpsellsSwiper();
}

function buildUpsellsHTML(products, heading, imageRatio, currencyCode) {
	let paddingBottom = "125%";
	if (imageRatio === "landscape") paddingBottom = "70%";
	else if (imageRatio === "square") paddingBottom = "100%";

	const slides = products
		.map((product) => {
			const variant = product.variants?.[0];
			const variantId = variant?.id || "";
			const image = product.featured_image || product.images?.[0];
			const imageUrl = image
				? typeof image === "string"
					? image
					: image.src || ""
				: "";
			const imgSized = imageUrl
				? imageUrl.replace(
						/(\.[a-z]+)(\?|$)/i,
						"_300x$1$2"
					)
				: "";

			const price = formatMoney(product.price, currencyCode);
			const comparePrice =
				product.compare_at_price && product.compare_at_price > product.price
					? formatMoney(product.compare_at_price, currencyCode)
					: null;

			let priceHTML = comparePrice
				? `<s class="cart-upsell-card__compare-price">${comparePrice}</s>
				   <span class="cart-upsell-card__sale-price">${price}</span>`
				: `<span>${price}</span>`;

			return `
			<div class="swiper-slide">
				<div class="cart-upsell-card">
					<a href="${product.url}" class="cart-upsell-card__link">
						<div class="cart-upsell-card__media" style="padding-bottom: ${paddingBottom};">
							${
								imgSized
									? `<img src="${imgSized}" alt="${escapeHTML(product.title)}" width="300" height="auto" loading="lazy" class="cart-upsell-card__image">`
									: ""
							}
						</div>
						<span class="cart-upsell-card__title">${escapeHTML(product.title)}</span>
					</a>
					<div class="cart-upsell-card__price">${priceHTML}</div>
					${
						product.available !== false
							? `<button type="button" class="cart-upsell-card__add" data-variant-id="${variantId}"><span>Add</span></button>`
							: `<button type="button" class="cart-upsell-card__add" disabled><span>Sold Out</span></button>`
					}
				</div>
			</div>`;
		})
		.join("");

	return `
		${heading ? `<h3 class="cart-upsells__heading">${escapeHTML(heading)}</h3>` : ""}
		<div class="cart-upsells__slider swiper">
			<div class="swiper-wrapper">${slides}</div>
		</div>
		<div class="cart-upsells__nav">
			<button class="cart-upsells__prev" aria-label="Previous">
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 14" width="8" height="8"><path d="M7 1L1 7l6 6" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
			</button>
			<button class="cart-upsells__next" aria-label="Next">
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 14" width="8" height="8"><path d="M1 1l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
			</button>
		</div>`;
}

function formatMoney(cents, withCurrencyCode) {
	if (typeof cents !== "number") cents = parseInt(cents, 10) || 0;
	const amount = (cents / 100).toFixed(2);
	const currency = window.Shopify?.currency?.active || "GBP";
	const symbol = { GBP: "\u00a3", USD: "$", EUR: "\u20ac" }[currency] || "";
	if (withCurrencyCode) {
		return `${symbol}${amount} ${currency}`;
	}
	return `${symbol}${amount}`;
}

function escapeHTML(str) {
	const div = document.createElement("div");
	div.textContent = str || "";
	return div.innerHTML;
}

// Refetch cart drawer + icon sections and update the DOM cleanly
function refreshCartDrawer() {
	return Promise.all([
		fetch(routes.cart_url + "?section_id=cart-drawer").then(function (r) { return r.text(); }),
		fetch(routes.cart_url + "?section_id=cart-icon-bubble").then(function (r) { return r.text(); }),
	]).then(function (results) {
		var drawerHTML = results[0];
		var iconHTML = results[1];
		var parser = new DOMParser();

		var drawerDoc = parser.parseFromString(drawerHTML, "text/html");
		var newDrawerInner = drawerDoc.querySelector(".drawer__inner");
		var currentDrawerInner = document.querySelector(".drawer__inner");
		if (newDrawerInner && currentDrawerInner) {
			currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
			currentDrawerInner.classList.remove("is-empty");

			var notificationEl = document.querySelector(".notification");
			if (notificationEl) {
				notificationEl.classList.remove("notification-open");
			}
			document.querySelectorAll(".cart-item.active").forEach(function (el) {
				el.classList.remove("active");
			});
		}

		var iconDoc = parser.parseFromString(iconHTML, "text/html");
		var newIcon = iconDoc.querySelector("#cart-icon-bubble");
		var currentIcon = document.querySelector("#cart-icon-bubble");
		if (newIcon && currentIcon) {
			currentIcon.innerHTML = newIcon.innerHTML;
		}

		setTimeout(function () {
			initCartUpsellsSwiper();
			fetchCartUpsellsFallback();
		}, 100);
	});
}

// Add-to-cart handler (delegated)
// Bypasses renderContents() to avoid notification mode — does a clean section refetch.
document.addEventListener("click", function (e) {
	const addButton = e.target.closest(".cart-upsell-card__add");
	if (!addButton || addButton.disabled) return;

	e.preventDefault();
	const variantId = addButton.dataset.variantId;
	if (!variantId) return;

	addButton.classList.add("loading");
	addButton.disabled = true;

	console.log('[Cart Upsells] Adding variant:', variantId);

	// Step 1: Add to cart (no sections needed — we'll refetch separately)
	fetch(routes.cart_add_url, {
		...fetchConfig(),
		body: JSON.stringify({
			items: [{ id: parseInt(variantId, 10), quantity: 1 }],
		}),
	})
		.then((res) => res.json())
		.then((response) => {
			console.log('[Cart Upsells] Add response:', response);

			// Handle errors — Shopify returns { status: 422 } or { errors: "..." }
			if (response.status || response.errors) {
				var errorMsg = response.description || response.errors || response.message;

				// If minimum quantity error, retry with the required minimum
				var minMatch = typeof errorMsg === 'string' && errorMsg.match(/minimum of (\d+)/);
				if (minMatch) {
					var minQty = parseInt(minMatch[1], 10);
					console.log('[Cart Upsells] Retrying with minimum quantity:', minQty);
					return fetch(routes.cart_add_url, {
						...fetchConfig(),
						body: JSON.stringify({
							items: [{ id: parseInt(variantId, 10), quantity: minQty }],
						}),
					})
						.then(function (r) { return r.json(); })
						.then(function (retryResponse) {
							if (retryResponse.status || retryResponse.errors) {
								addButton.classList.remove("loading");
								addButton.disabled = false;
								return;
							}
							publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-upsells", cartData: retryResponse });
							return refreshCartDrawer();
						});
				}

				publish(PUB_SUB_EVENTS.cartError, {
					source: "cart-upsells",
					errors: errorMsg,
				});
				addButton.classList.remove("loading");
				addButton.disabled = false;
				return;
			}

			publish(PUB_SUB_EVENTS.cartUpdate, {
				source: "cart-upsells",
				cartData: response,
			});

			// Step 2: Refetch cart drawer cleanly
			refreshCartDrawer();
		})
		.catch((e) => {
			console.error("Cart upsells: add to cart failed", e);
			addButton.classList.remove("loading");
			addButton.disabled = false;
		});
});

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", function () {
	initCartUpsellsSwiper();
	fetchCartUpsellsFallback();

	// Re-init after any cart change (add, remove, quantity update)
	// Uses pubsub which fires after all DOM updates are complete.
	let upsellReinitTimer;
	subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
		clearTimeout(upsellReinitTimer);
		upsellReinitTimer = setTimeout(function () {
			initCartUpsellsSwiper();
			fetchCartUpsellsFallback();
		}, 150);
	});
});
